import { supabase } from './supabase'
import type { OnlineStoreState } from '../types'

// Client API for the online-store paid add-on (mig 332–336). READS go through the
// member RPC get_online_store_status; the trial start goes through the owner RPC
// activate_online_store; product visibility through set_products_online_visible.
// Renewal is MANUAL: the merchant pays via the platform Paystack popup and the
// webhook calls extend_online_store_after_payment — there is no client charge path.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rpc = (name: string, args: Record<string, unknown>): any =>
  (supabase as any).rpc(name, args)

export const STOREFRONT_BASE_DOMAIN = 'palstro.com'

export async function fetchOnlineStoreStatus(tenantId: string): Promise<OnlineStoreState> {
  const { data, error } = await rpc('get_online_store_status', { p_tenant_id: tenantId })
  if (error) throw error
  return data as OnlineStoreState
}

export type ActivateResult =
  | { ok: true; already_active: boolean; status: string; trial_days?: number; expires_at: string }
  | { ok: false; reason: string }

export async function activateOnlineStore(tenantId: string): Promise<ActivateResult> {
  const { data, error } = await rpc('activate_online_store', { p_tenant_id: tenantId })
  if (error) throw error
  return data as ActivateResult
}

// Server-computed price + resulting expiry for a chosen number of months (mig 392).
// The SINGLE source of the add-on amount: amount = ROUND(months × admin price ÷
// admin period). The UI displays this and pays exactly it — it does no maths.
export type OnlineStoreQuote =
  | {
      valid: true
      months: number
      amount: number
      per_month: number
      price_ngn: number
      period_months: number
      current_expires_at: string | null
      new_expiry: string
    }
  | { valid: false; reason: string; min: number; max: number; price_ngn: number; period_months: number; per_month: number }

export async function quoteOnlineStore(tenantId: string, months: number): Promise<OnlineStoreQuote> {
  const { data, error } = await rpc('quote_online_store', { p_tenant_id: tenantId, p_months: months })
  if (error) throw error
  return data as OnlineStoreQuote
}

export async function setProductsOnlineVisible(
  tenantId: string,
  productIds: string[],
  visible = true,
): Promise<{ ok: boolean; updated: number }> {
  const { data, error } = await rpc('set_products_online_visible', {
    p_tenant_id: tenantId,
    p_product_ids: productIds,
    p_visible: visible,
  })
  if (error) throw error
  return data as { ok: boolean; updated: number }
}

// First-activation default: mark all active in-stock products online in one call.
export async function defaultInstockProductsOnline(tenantId: string): Promise<{ ok: boolean; updated: number }> {
  const { data, error } = await rpc('default_instock_products_online', { p_tenant_id: tenantId })
  if (error) throw error
  return data as { ok: boolean; updated: number }
}

// Owner/admin storefront on/off — the L7.6 commerce_enabled master toggle. Returns the
// merged storefront_settings blob (for setTenant). This is the "Deactivate store" switch.
export async function setStorefrontLive(tenantId: string, live: boolean): Promise<Record<string, unknown>> {
  const { data, error } = await rpc('set_storefront_settings', {
    p_tenant_id: tenantId,
    p_patch: { commerce_enabled: live },
  })
  if (error) throw error
  return (data ?? {}) as Record<string, unknown>
}

// Human copy for the reasons activate_online_store can refuse with.
export function activateErrorMessage(reason: string | undefined): string {
  switch (reason) {
    case 'no_active_plan':
      return 'You need an active paid Palstro plan before you can add the Online Store.'
    case 'addon_disabled':
      return 'The Online Store add-on isn’t available right now. Please try again later.'
    case 'trial_used':
      return 'Your free trial has already been used. Renew to bring your store back online.'
    default:
      return 'Something went wrong. Please try again.'
  }
}

// Days left until the add-on period end (ceil, floored at 0). null when no expiry.
export function daysUntil(expiresAt: string | null | undefined): number | null {
  if (!expiresAt) return null
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000))
}

export type PillTone = 'green' | 'red' | 'amber'

export interface OnlineStoreDisplay {
  configured: boolean   // ever activated (status !== inactive)
  online: boolean       // EFFECTIVE: add-on live AND owner toggle (commerce_enabled) on
  tone: PillTone
  label: string
  blink: boolean        // red + blinking in the last 5 days
  daysLeft: number | null
}

// Single source of truth for the pill + details page. `commerceEnabled` is the L7.6
// owner toggle (tenant.storefront_settings.commerce_enabled), fail-open (unset = on).
export function deriveOnlineStoreDisplay(
  os: OnlineStoreState,
  commerceEnabled: boolean | undefined,
): OnlineStoreDisplay {
  const commerceOn = commerceEnabled ?? true
  const d = daysUntil(os.expires_at)

  if (os.status === 'inactive') {
    return { configured: false, online: false, tone: 'amber', label: 'Online store: Off', blink: false, daysLeft: null }
  }
  if (os.status === 'expired') {
    return { configured: true, online: false, tone: 'red', label: 'Store offline', blink: false, daysLeft: 0 }
  }

  // Live add-on (trialing | active).
  if (!commerceOn) {
    return { configured: true, online: false, tone: 'red', label: 'Store offline', blink: false, daysLeft: d }
  }
  const nearEnd = d !== null && d <= 5
  const isTrial = os.status === 'trialing'
  return {
    configured: true,
    online: true,
    tone: nearEnd ? 'red' : isTrial ? 'amber' : 'green',
    label: isTrial ? 'Store trial' : 'Store online',
    blink: nearEnd,
    daysLeft: d,
  }
}
